import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Popconfirm,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiEyeDuotone,
	PiHouseDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import {
	requestGetServiceGroupList,
	requestDeleteServiceGroup,
} from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";

const { Title } = Typography;
const { Option } = Select;

function ServiceGroupsListPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [serviceGroups, setServiceGroups] = useState([]);
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page") || "1", 10),
		pageSize: parseInt(searchParams.get("limit") || "10", 10),
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		status: searchParams.get("status") || "",
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Service Groups";
		fetchServiceGroups();
	}, []);

	useEffect(() => {
		fetchServiceGroups();
	}, [pagination.current, pagination.pageSize, filters]);

	// Synchronize state with URL parameters
	useEffect(() => {
		const params = new URLSearchParams();

		if (pagination.current !== 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.status) params.set("status", filters.status);

		setSearchParams(params);
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchServiceGroups = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		if (filters.status) {
			filterParts.push(`status:${filters.status}`);
		}
		// Add more filter fields here as needed

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: filterString,
			order: "name:asc",
		};

		requestGetServiceGroupList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const serviceGroupsData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setServiceGroups(serviceGroupsData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching service groups:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch service groups",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleDeleteServiceGroup = (id) => {
		requestDeleteServiceGroup(id)
			.then(() => {
				message.success("Service group deleted successfully");
				fetchServiceGroups();
			})
			.catch((error) => {
				console.error("Error deleting service group:", error);
				if (error.response?.data?.message) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} else {
					notification.error({
						message: "Error",
						description: "Failed to delete service group. Please try again.",
					});
				}
			});
	};

	const handleTableChange = (newPagination) => {
		setPagination(newPagination);
	};

	const handleSearch = (keyword) => {
		setFilters((prev) => ({ ...prev, keyword }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleStatusFilter = (status) => {
		setFilters((prev) => ({ ...prev, status }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>Code: {record.code}</div>
				</div>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			render: (text) => text || "-",
		},
		{
			title: "Created At",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Action",
			key: "action",
			align: "right",
			render: (_, record) => (
				<Space size="middle">
					<Button
						type="text"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/service-groups/${record._id}`)}
						title="View"
					/>
					<Button
						type="text"
						size="small"
						icon={<PiPencilDuotone />}
						onClick={() => navigate(`/service-groups/${record._id}/edit`)}
						title="Edit"
					/>
					<Popconfirm
						title="Delete Service Group"
						description="Are you sure you want to delete this service group?"
						onConfirm={() => handleDeleteServiceGroup(record._id)}
						okText="Yes"
						cancelText="No"
						okType="danger">
						<Button type="text" size="small" danger icon={<PiTrashDuotone />} title="Delete" />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Service Groups
					</Title>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="Service Groups"
					extra={
						<Button
							type="primary"
							icon={<PiPlusDuotone />}
							onClick={() => navigate("/service-groups/add")}>
							Add Service Group
						</Button>
					}>
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={12}>
							<SearchInput
								placeholder="Search service groups..."
								value={filters.keyword}
								onSearch={handleSearch}
								style={{ width: "100%" }}
							/>
						</Col>
						<Col span={6}>
							<Select
								placeholder="Filter by status"
								value={filters.status}
								onChange={handleStatusFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="ACTIVE">Active</Option>
								<Option value="INACTIVE">Inactive</Option>
							</Select>
						</Col>
					</Row>
					<Table
						columns={columns}
						dataSource={serviceGroups}
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} service groups`,
						}}
						onChange={handleTableChange}
						rowKey="_id"
					/>
				</Card>
			</div>
		</div>
	);
}

export default ServiceGroupsListPage;
